import React, { useEffect, useState } from "react";
import AppLoading from "../components/AppLoading";
import TableGraph from "../components/Charts/comps/TableGraph";
import { PagesCommonProps } from "../helpers/pagesUtil";
import DrillDownV2 from "../components/drillDowns/DrillDownV2";
import dashboardAdminAPIsService from "../hooks/dashboardAdminAPIs";
import SummaryCardContainer from "../components/SummaryCardsContainer";
import { mergeChartConfigWithApiData } from "../helpers/chartConfigHelper";
import { dashboardGraphsOptions, GOOGLE_CHARTS_TYPES } from "../consts/graphs";
import ChartContainer, { ChartContainerProps } from "../components/Charts/ChartContainer";
import { mostAndLeastUsersOtherActions, ragStatusOtherActions } from "../helpers/page2";
import useFetchManagerDashboardData from "../hooks/fetchDataComponents/useFetchManagerDashboardData";
import { selectedDataRowForTableDefaultState, showEstimatedHoursDrillDown, estimatedHoursDrillDownOtherActions } from "../helpers/page1";

const { BAR, AREACHART, BARCHART, COLUMNCHART, TABLE } = GOOGLE_CHARTS_TYPES;

const ManagerDashboardGraphs: React.FC<PagesCommonProps> = ({ filters, graphs = [] }) => {
  const {
    data,
    onScroll,
    onSortChange,
    isLoadingMap,
    endpointsMap,
    otherActions,
    exportActions,
    paginationActions,
  } = useFetchManagerDashboardData({ filters, graphs });
  const [hoveredRowStye, setHoveredRowStye] = useState<{top?: number, left?: number, width?: string} | null>(null);
  const [selectedDataRowForTable, setSelectedDataRowForTable] = useState(selectedDataRowForTableDefaultState);
  const [isDrillDownLoading, setIsDrillDownLoading] = useState(false);
  const [currentDrillType, setCurrentDrillType] = useState<"site" | "course">("course");
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  useEffect(() => {setHoveredRowStye(null)}, [filters]);
  
  // Wrapper function to adapt EstimatedHours API response to DrillDownV2 format
  const fetchEstimatedHoursMainData = async (
    params: Record<string, unknown>,
    signal?: AbortSignal
  ) => {
    const response = await dashboardAdminAPIsService.fetchEstimatedHoursSpentOnElearningDrillDownData(
      {
        estimatedHoursDrillDownBy: params.type as string,
        page: 1,
        limit: 10000,
        ...params
      } as any,
      signal
    );
    
    // Map the response to match DrillDownV2 expected format
    const mappedData = (response.data || []).map((item: any) => ({
      name: item.title || item.name || `No ${params.type}`,
      completed: 0,
      noOfCompleted: 0,
      incomplete: 0,
      noOfIncomplete: 0,
      total: item.totalTime || 0,
      // Store original data for custom display
      _original: item
    }));
    
    return { data: mappedData };
  };

  if (isLoadingMap?.fetchSummaryCardsData) return <AppLoading />;

  const baseChartsConfig: ChartContainerProps[] = [
    {
      boxWidth: "95%",
      chartType: AREACHART,
      title: 'Users Compliance Trend',
      chartId: "fetchUsersComplianceTrendData",
      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
        data?.fetchUsersComplianceTrendData?.data?.[0],
        { v: "% of Completed Courses" }
      ),
    },
    {
      onScroll,
      onSortChange,
      boxWidth: "45%",
      chartType: TABLE,
      showXlsExport: true,
      ragColoringColumns: [3],
      title: 'Compliance by User',
      chartId: "fetchUsersCompliancePercentageData",
      otherActions: ragStatusOtherActions("fetchUsersCompliancePercentageData", otherActions, data),
      options: dashboardGraphsOptions.TableChartOptions,
    },/*
    {
      onScroll,
      onSortChange,
      boxWidth: "45%",
      chartType: TABLE,
      title: 'No. of Employees Overdue',
      chartId: "fetchNumberOfEmployeesOverdueData",

      options: dashboardGraphsOptions.TableChartOptions,
    },*/
    {
      boxWidth: "45%",
      chartType: COLUMNCHART,
      title: 'Course Completion by Month',
      chartId: "fetchCourseCompletionByMonthData",
      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
        data?.fetchCourseCompletionByMonthData?.data?.[0],
        { v: "No. of Courses", h: " "}
      ),
      /*onRowSelect: (param: any) => showCourseNamesOnRowSelect(
        "fetchCourseCompletionByMonthData", param,
        (data?.fetchCourseCompletionByMonthData as any),
        setHoveredRowStye, setSelectedDataRowForTable,
      ),*/
    },
    {
      onScroll,
      onSortChange,
      boxWidth: "25%",
      chartType: TABLE,
      title: 'Active User Never Logged In',
      chartId: "fetchActiveUsersNeverLoggedInData",

      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
        data?.fetchActiveUsersNeverLoggedInData?.data?.[0],
      ),
      /*onRowSelect: (param: any) => showUserNamesOnRowSelect(
        "fetchUsersNeverLoggedInData", param,
        (data?.fetchUsersNeverLoggedInData as any),
        setHoveredRowStye, setSelectedDataRowForTable,
      ),*/
    },
    {
      boxWidth: "95%",
      chartType: COLUMNCHART,
      title: 'Estimated Hours Spent on eLearning',
      chartId: "fetchEstimatedHoursSpentOnElearningData",
      AdditionalDialogComp: <DrillDownV2
        total={
          (data?.fetchEstimatedHoursSpentOnElearningData as any)?.totalSum || 0
        }
        filters={filters}
        fetchMainData={fetchEstimatedHoursMainData}
        drilldownTypes={[
          {
            label: 'Course',
            value: 'course',
            fieldName: 'id',
            paramName: 'parentId',
            typeValue: 'course'
          },
          {
            label: 'Site',
            value: 'site',
            fieldName: 'id',
            paramName: 'parentId',
            typeValue: 'site'
          }
        ]}
        enableHoverPieChart={false}
        exportFilenamePrefix="estimated-hours-drilldown-export"
      />,
      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
        data?.fetchEstimatedHoursSpentOnElearningData?.data?.[0],
      ),
      onRowSelect: (param: any) => showEstimatedHoursDrillDown(
        "fetchEstimatedHoursSpentOnElearningData", param,
        (data?.fetchEstimatedHoursSpentOnElearningData as any),
        setHoveredRowStye, setSelectedDataRowForTable, setIsDrillDownLoading,
        setCurrentDrillType, setSelectedMonth,
        (drillDownFilters: any) => dashboardAdminAPIsService.fetchEstimatedHoursSpentOnElearningDrillDownData({ ...filters, ...drillDownFilters })
      ),
    },
    {
      boxWidth: "45%",
      chartType: BARCHART,
      paginationActions,
      showXlsExport: true,
      ragColoringColumns: [1],
      title: `Most Compliant Courses`,
      chartId: "fetchMostLeastCompliantCoursesData",
      otherActions: [
        ...mostAndLeastUsersOtherActions("fetchMostLeastCompliantCoursesData", otherActions, data),
        ...ragStatusOtherActions("fetchMostLeastCompliantCoursesData", otherActions, data),
      ],
      options: (() => {
        const baseOptions =
          dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
            data?.fetchMostLeastCompliantCoursesData?.data?.[0]
          );
        return {
          ...baseOptions,
          chartArea: {
            ...baseOptions.chartArea,
            left: 100,
            height: '80%',
          },
        };
      })(),
    },/*
    {
      boxWidth: "45%",
      chartType: BARCHART,
      chartId: "fetchDocumentsOutstandingData",
      title: "Documents Outstanding",
      AdditionalDialogComp: <DrillDownV2
        total={
          (data?.fetchSummaryCardsData as any)?.documentsCount || 0
        }
        filters={filters}
        fetchMainData={dashboardAdminAPIsService.fetchDocumentsBreakdownDrillDownData}
        fetchSubData={async () => ({ data: [] })} // Documents drilldown doesn't have sub-data
        drilldownTypes={[]}
        exportFilenamePrefix="documents-drilldown-export"
      />,
      options: {
        ...dashboardGraphsOptions.BarChartChartOptions.GetByColumnLength(
          data?.fetchDocumentsOutstandingData?.data?.[0],
          { h: "No of documents read/unread" }
        ),
        isStacked: true,
        hAxis: {
        format: "#",
        title: '',
        textStyle: { fontSize: 13 },
        ticks: [0, 25, 50, 75, 100],
        titleTextStyle: { fontSize: 13 },
      },
      },
    },*/
    {
      boxWidth: "95%",
      chartType: BAR,
      title: 'Logins by Month',
      chartId: "fetchLoginsByMonthData",

      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(data?.fetchLoginsByMonthData?.data?.[0]),
    },
    {
      boxWidth: "50%",
      chartType: BAR,
      title: 'Logins by Day',
      chartId: "fetchLoginsByDayData",

      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(data?.fetchLoginsByDayData?.data?.[0]),
    },
    {
      boxWidth: "35%",
      chartType: AREACHART,
      title: 'Logins by Time',
      chartId: "fetchLoginsByTimeData",

      options: {
        ...dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
        data?.fetchLoginsByTimeData?.data?.[0],
      ),
      isStacked: false,
      }
    },
  ];

  const ChartsListData: ChartContainerProps[] = mergeChartConfigWithApiData(baseChartsConfig, graphs);

  return (
    <>
      <SummaryCardContainer summaryCardsData={data?.fetchSummaryCardsData?.data as any} />
      <div className="charts-container">
        {ChartsListData.map((props, idx) => {
          const chartId = props.chartId;
          if (!chartId) return null;
          return (
            <ChartContainer 
              {...props} 
              key={idx} 
              index={idx}
              queryParams={filters}
              exportActions={exportActions}
              data={data?.[chartId as keyof typeof data]?.data}
              total={data?.[chartId as keyof typeof data]?.total}
              endpoint={endpointsMap?.[chartId as keyof typeof endpointsMap]}
              isLoading={isLoadingMap?.[chartId as keyof typeof isLoadingMap]}
            />
          );
        })}
      </div>
      {hoveredRowStye !== null && (
        <div className='chart-hovered' style={hoveredRowStye}>
          <div className='chart-hovered-buttons'>
            {estimatedHoursDrillDownOtherActions(
              "fetchEstimatedHoursSpentOnElearningData",
              currentDrillType,
              setCurrentDrillType,
              selectedMonth,
              setSelectedDataRowForTable,
              setIsDrillDownLoading,
              (drillDownFilters: any) => dashboardAdminAPIsService.fetchEstimatedHoursSpentOnElearningDrillDownData({ ...filters, ...drillDownFilters })
            ).map(({ text, action, style: buttonStyle }, idx) => (
              <button
                key={idx}
                className="action-button"
                onClick={action}
                style={buttonStyle || {}}
              >
                {text}
              </button>
            ))}
            <button
              className="close-button"
              about="tools"
              onClick={() => {
                setHoveredRowStye(null);
                setIsDrillDownLoading(false);
              }}
              style={{
                backgroundSize: '13px',
                backgroundImage: `url('${process.env.REACT_APP_PUBLIC_URL || process.env.PUBLIC_URL || ''}/close.png')`,
              }}
            />
          </div>
          {isDrillDownLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '200px',
              flexDirection: 'column',
              color: 'black'
            }}>
              <div style={{ marginBottom: '10px' }}>Loading {currentDrillType === "site" ? "site" : "course"} details...</div>
              <div className="spinner" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
          ) : (
            <div style={{ width: hoveredRowStye.width || "200px", height: "200px", color: "black" }}>
              <TableGraph data={selectedDataRowForTable} isFullHeight />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ManagerDashboardGraphs;
